import { useEffect, useEffectEvent, useId, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Tour } from '../tours'
import { Button } from './Button'

type Box = { top: number; left: number; width: number; height: number }

/** The first visible element carrying the step's `data-tour` name. */
function findTarget(name: string): HTMLElement | undefined {
  return [...document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(name)}"]`)].find(
    (el) => el.getClientRects().length > 0,
  )
}

const same = (a: Box | undefined, b: Box) =>
  a !== undefined &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height

/** Stable, so React calls it only when a card mounts, not on every render. */
const focusOnce = (el: HTMLElement | null) => el?.focus()

const PAD = 6
const GAP = 12
const CARD_WIDTH = 320

/**
 * Plays a tour: opens each step's page, lights up the element to use, and
 * says what to do with it. Using the element itself also moves on, so the
 * tour follows what the person actually does. The page stays usable
 * throughout; Escape or End leaves it.
 */
export function TourOverlay({ tour, onEnd }: { tour: Tour; onEnd: () => void }) {
  const [index, setIndex] = useState(0)
  // Where the target is, for the step it was found for: a new step starts unknown.
  const [found, setFound] = useState<{ index: number; box: Box | 'missing' }>()
  const box = found?.index === index ? found.box : undefined
  const navigate = useNavigate()
  const location = useLocation()
  const titleId = useId()
  const step = tour.steps[index]
  const last = index === tour.steps.length - 1

  const next = () => (last ? onEnd() : setIndex((i) => i + 1))
  const advance = useEffectEvent(next)
  // Only when the step changes: going elsewhere within a step is the person's choice.
  const openPage = useEffectEvent((path: string) => {
    if (location.pathname !== path) navigate(path)
  })

  // Open the step's page, find the target, keep the light on it as the page
  // moves, and move on when it is used.
  useEffect(() => {
    const current = tour.steps[index]
    if (!current) return
    openPage(current.path)
    const started = performance.now()
    let target: HTMLElement | undefined
    let shown: Box | undefined
    let missing = false
    let frame = 0
    const onUse = () => window.setTimeout(advance, 0)
    const track = () => {
      const el = findTarget(current.target)
      if (el !== target) {
        target?.removeEventListener('click', onUse)
        target = el
        target?.addEventListener('click', onUse)
        const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches
        target?.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' })
      }
      if (el) {
        const r = el.getBoundingClientRect()
        const b = { top: r.top, left: r.left, width: r.width, height: r.height }
        if (!same(shown, b)) setFound({ index, box: (shown = b) })
        missing = false
      } else {
        // Gone: forget where it was, so it is lit again when it comes back,
        // and say so once rather than on every frame.
        shown = undefined
        if (!missing && performance.now() - started > 4000) {
          missing = true
          setFound({ index, box: 'missing' })
        }
      }
      frame = requestAnimationFrame(track)
    }
    frame = requestAnimationFrame(track)
    return () => {
      cancelAnimationFrame(frame)
      target?.removeEventListener('click', onUse)
    }
  }, [index, tour])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onEnd()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onEnd])

  if (!step) return null

  // Below the target when there is room, above it otherwise, and at the foot
  // of the window when it fills the window; centred when it is not found.
  const width = Math.min(CARD_WIDTH, window.innerWidth - 2 * GAP)
  const room = 200
  let position: { left: number; top?: number; bottom?: number }
  if (box && box !== 'missing') {
    const left = Math.min(Math.max(GAP, box.left), window.innerWidth - width - GAP)
    const below = box.top + box.height + PAD + GAP
    if (below + room < window.innerHeight) position = { left, top: below }
    else if (box.top - PAD - GAP > room)
      position = { left, bottom: window.innerHeight - box.top + PAD + GAP }
    else position = { left, bottom: GAP }
  } else {
    position = { left: (window.innerWidth - width) / 2, top: window.innerHeight / 3 }
  }

  return (
    <div className="tour">
      {box && box !== 'missing' && (
        <div
          className="tour-spotlight"
          aria-hidden="true"
          style={{
            top: box.top - PAD,
            left: box.left - PAD,
            width: box.width + 2 * PAD,
            height: box.height + 2 * PAD,
          }}
        />
      )}
      <div
        // A new card for each step, focused once as it appears, so a screen
        // reader reads the step; never again while the person works the page.
        key={index}
        ref={focusOnce}
        className="tour-card"
        role="dialog"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ ...position, width }}
      >
        <p id={titleId} className="tour-title">
          {tour.title}
          <span className="muted">
            {' '}
            Step {index + 1} of {tour.steps.length}
          </span>
        </p>
        <p className="tour-text" aria-live="polite">
          {step.text}
        </p>
        {box === 'missing' && (
          <p className="small muted">
            That part is not on this page at the moment. It may appear once assessments are loaded.
          </p>
        )}
        <div className="row items-center gap-2">
          <Button tone="quiet" size="small" label="End" onClick={onEnd} />
          <span className="grow" />
          {index > 0 && (
            <Button tone="quiet" size="small" label="Back" onClick={() => setIndex(index - 1)} />
          )}
          {step.clickToContinue && box !== 'missing' ? (
            <span className="small muted">Use the highlighted control to go on.</span>
          ) : (
            <Button tone="primary" size="small" label={last ? 'Done' : 'Next'} onClick={next} />
          )}
        </div>
      </div>
    </div>
  )
}
